export const PROMPT_CATEGORIES = [
  "Executive",
  "Preconstruction",
  "Project Management",
  "Field Operations",
  "Commercial/Contracts",
  "Safety/Quality",
] as const;

export type CanonicalPromptCategory = (typeof PROMPT_CATEGORIES)[number];

// Spaced labels remain accepted for compatibility with existing UI display maps.
export type PromptCategory =
  | CanonicalPromptCategory
  | "Commercial / Contracts"
  | "Safety / Quality";

export type PromptRole =
  | "Construction Executive"
  | "Preconstruction Manager"
  | "Project Manager"
  | "Superintendent"
  | "Commercial Manager"
  | "Safety and Quality Leader";

export interface PromptVariable {
  readonly key: string;
  readonly label: string;
  readonly placeholder: string;
  readonly required: boolean;
}

export interface ConstructionPrompt {
  readonly id: string;
  readonly title: string;
  readonly outcome: string;
  readonly role: PromptRole;
  readonly category: PromptCategory;
  readonly tags: readonly string[];
  readonly estimatedMinutes: number;
  readonly featured: boolean;
  readonly variables: readonly PromptVariable[];
  readonly prompt: string;
  readonly tips: readonly [string, string, string];
}

export const constructionPrompts = [
  {
    id: "executive-decision-brief",
    title: "Build an Executive Decision Brief",
    outcome: "A one-page brief that frames a project decision, its exposure, and the recommended action.",
    role: "Construction Executive",
    category: "Executive",
    tags: ["decision", "risk", "leadership", "briefing"],
    estimatedMinutes: 8,
    featured: true,
    variables: [
      { key: "decision", label: "Decision required", placeholder: "Authorize temporary enclosure for Levels 14-19", required: true },
      { key: "context", label: "Project context", placeholder: "Facade fabrication is forecast 14 days late", required: true },
      { key: "options", label: "Options considered", placeholder: "Accelerate, resequence, or temporarily enclose", required: true },
      { key: "deadline", label: "Decision deadline", placeholder: "Friday at 3:00 PM", required: true },
      { key: "evidence", label: "Known evidence", placeholder: "Schedule update, supplier notice, cost model, lease milestone", required: true },
    ],
    prompt: `Act as a construction executive preparing a decision for accountable approval.

Decision required: {{decision}}
Project context: {{context}}
Options considered: {{options}}
Decision deadline: {{deadline}}
Available evidence: {{evidence}}

Create a concise executive decision brief with: decision requested; current condition; business and project exposure; options with cost, schedule, risk, and reversibility; recommended action; assumptions; unresolved facts; approval limit; owner; and immediate next steps. Separate verified facts from forecasts and judgment. State what happens if the decision is deferred. Use direct language and keep the main brief under 500 words.`,
    tips: [
      "Replace estimates with validated project controls data before circulation.",
      "Name the accountable approver and the exact authorization limit.",
      "Keep supporting analysis in an appendix rather than the decision page.",
    ],
  },
  {
    id: "executive-portfolio-risk-review",
    title: "Prepare a Portfolio Risk Review",
    outcome: "A ranked portfolio view that directs executive attention to projects requiring intervention.",
    role: "Construction Executive",
    category: "Executive",
    tags: ["portfolio", "risk", "governance", "forecast"],
    estimatedMinutes: 12,
    featured: false,
    variables: [
      { key: "reporting_period", label: "Reporting period", placeholder: "September 2026", required: true },
      { key: "project_updates", label: "Project updates", placeholder: "Paste current cost, schedule, safety, quality, and commercial updates", required: true },
      { key: "thresholds", label: "Escalation thresholds", placeholder: ">$250K variance, >10 working days, or material client dispute", required: true },
      { key: "prior_commitments", label: "Prior commitments", placeholder: "Actions and owners agreed at the last review", required: false },
    ],
    prompt: `Act as a construction portfolio leader reviewing performance for {{reporting_period}}.

Project updates: {{project_updates}}
Executive escalation thresholds: {{thresholds}}
Prior commitments: {{prior_commitments}}

Produce a portfolio risk review. Rank only the projects requiring executive attention. For each, show the changed condition, quantified exposure, confidence in the forecast, root issue, accountable owner, decision or intervention needed, and decision date. Identify cross-project patterns involving labor, procurement, cash, claims, safety, or client relationships. Close with the five actions leadership should verify at the next review. Do not invent missing figures; label missing information and assign a validation action.`,
    tips: [
      "Use consistent reporting dates and definitions across every project.",
      "Escalate changed exposure, not routine status information.",
      "Confirm sensitive commercial assessments with project leadership before distribution.",
    ],
  },
  {
    id: "executive-go-no-go-review",
    title: "Run a Pursuit Go/No-Go Review",
    outcome: "A disciplined pursuit recommendation based on strategic fit, delivery capacity, and risk-adjusted value.",
    role: "Construction Executive",
    category: "Executive",
    tags: ["pursuit", "go-no-go", "strategy", "risk"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "opportunity", label: "Opportunity", placeholder: "$180M healthcare expansion in Phoenix", required: true },
      { key: "client", label: "Client and relationship", placeholder: "Repeat client; strong operations relationship", required: true },
      { key: "delivery_terms", label: "Delivery model and terms", placeholder: "CMAR with GMP; liquidated damages under negotiation", required: true },
      { key: "capacity", label: "Team and market capacity", placeholder: "Available PX; limited MEP estimating capacity", required: true },
      { key: "economics", label: "Pursuit economics", placeholder: "Fee target, win probability, pursuit cost, backlog need", required: true },
    ],
    prompt: `Act as an executive construction pursuit committee.

Opportunity: {{opportunity}}
Client and relationship: {{client}}
Delivery model and proposed terms: {{delivery_terms}}
Team and market capacity: {{capacity}}
Pursuit economics: {{economics}}

Prepare a go/no-go assessment covering strategic fit, client quality, delivery capability, competitive position, commercial terms, schedule realism, supply-chain conditions, safety and quality demands, cash exposure, and opportunity cost. Score each area as favorable, manageable, or unacceptable and explain the evidence. Recommend GO, CONDITIONAL GO, or NO-GO. For a conditional recommendation, list the exact conditions, owner, and deadline required before further pursuit spending.`,
    tips: [
      "Use current staffing commitments rather than an ideal future team.",
      "Treat unresolved contract terms as conditions, not assumptions.",
      "Record the rationale so later pursuit decisions can be reviewed consistently.",
    ],
  },
  {
    id: "executive-recovery-governance",
    title: "Establish a Project Recovery Cadence",
    outcome: "A focused governance plan for stabilizing a distressed or deteriorating project.",
    role: "Construction Executive",
    category: "Executive",
    tags: ["recovery", "governance", "accountability", "turnaround"],
    estimatedMinutes: 10,
    featured: false,
    variables: [
      { key: "project_condition", label: "Current project condition", placeholder: "Eight weeks late with declining productivity and open design changes", required: true },
      { key: "critical_outcomes", label: "Critical outcomes", placeholder: "Protect TCO, stop margin erosion, preserve owner relationship", required: true },
      { key: "constraints", label: "Constraints", placeholder: "Occupied campus, limited laydown, fixed commissioning date", required: true },
      { key: "team", label: "Leadership team", placeholder: "Executive sponsor, PX, PM, superintendent, scheduler, commercial lead", required: true },
    ],
    prompt: `Act as the executive sponsor for a project requiring recovery.

Current condition: {{project_condition}}
Critical outcomes: {{critical_outcomes}}
Constraints: {{constraints}}
Leadership team: {{team}}

Design a 30-day recovery governance cadence. Define the few control metrics that indicate stabilization, the daily and weekly meeting rhythm, decision rights, escalation thresholds, required source data, named functional ownership, and a visible commitment log. Separate immediate containment actions from root-cause correction. Include a first-week agenda and criteria for returning the project to normal governance. Avoid adding meetings that do not produce a decision, commitment, or verified update.`,
    tips: [
      "Use measures the team can update from authoritative systems each cycle.",
      "Give every recovery commitment one owner and one due date.",
      "End the intensified cadence when explicit stabilization criteria are met.",
    ],
  },
  {
    id: "precon-basis-of-estimate-review",
    title: "Review a Basis of Estimate",
    outcome: "A structured review of estimate scope, assumptions, exclusions, allowances, and unresolved exposure.",
    role: "Preconstruction Manager",
    category: "Preconstruction",
    tags: ["estimate", "scope", "assumptions", "risk"],
    estimatedMinutes: 12,
    featured: true,
    variables: [
      { key: "estimate_stage", label: "Estimate stage", placeholder: "Design development GMP update", required: true },
      { key: "project_scope", label: "Project scope", placeholder: "420,000 SF mixed-use tower with five-level podium", required: true },
      { key: "estimate_notes", label: "Estimate notes", placeholder: "Paste basis, qualifications, allowances, and major quantities", required: true },
      { key: "design_status", label: "Design status", placeholder: "60% architectural, 40% MEP, structural issued", required: true },
      { key: "market_context", label: "Market context", placeholder: "Labor escalation and long-lead equipment conditions", required: false },
    ],
    prompt: `Act as a senior preconstruction manager performing an independent basis-of-estimate review.

Estimate stage: {{estimate_stage}}
Project scope: {{project_scope}}
Estimate notes: {{estimate_notes}}
Design status: {{design_status}}
Market context: {{market_context}}

Create a review organized by scope completeness, quantity basis, pricing basis, subcontractor coverage, labor assumptions, logistics, schedule, escalation, allowances, contingencies, exclusions, and owner-provided items. Identify contradictions, hidden scope interfaces, unsupported assumptions, and items likely to move before award. Rank the ten most material exposures by likely cost and confidence. Finish with clarification questions and named validation actions for the estimate team.`,
    tips: [
      "Provide estimate dates and drawing issue dates so timing assumptions are visible.",
      "Validate quantities and subcontractor coverage in the source estimate.",
      "Do not convert allowances into commitments without design and procurement support.",
    ],
  },
  {
    id: "precon-scope-gap-analysis",
    title: "Find Scope Gaps Between Bid Packages",
    outcome: "A trade-interface matrix identifying missing, duplicated, or ambiguously assigned work.",
    role: "Preconstruction Manager",
    category: "Preconstruction",
    tags: ["scope-gap", "bid-packages", "coordination", "procurement"],
    estimatedMinutes: 15,
    featured: false,
    variables: [
      { key: "packages", label: "Bid package scopes", placeholder: "Paste relevant trade scope narratives", required: true },
      { key: "design_requirements", label: "Design requirements", placeholder: "Key specification and drawing requirements", required: true },
      { key: "owner_requirements", label: "Owner requirements", placeholder: "Owner standards, vendor interfaces, and furnished equipment", required: false },
      { key: "project_constraints", label: "Project constraints", placeholder: "Phasing, access, temporary works, testing, and turnover constraints", required: true },
    ],
    prompt: `Act as a preconstruction manager coordinating trade scope.

Bid package scopes: {{packages}}
Design requirements: {{design_requirements}}
Owner requirements: {{owner_requirements}}
Project constraints: {{project_constraints}}

Build a scope-interface analysis. Identify missing work, duplicate coverage, ambiguous responsibility, temporary-work ownership, access and protection obligations, controls and power interfaces, testing and commissioning boundaries, permits, closeout, and owner-vendor coordination. Present findings as a matrix with issue, affected packages, likely owner, commercial consequence, schedule consequence, and clarification required. Prioritize gaps that could cause change orders or procurement delay.`,
    tips: [
      "Use issued scope exhibits and specifications, not bid-summary memory.",
      "Confirm final responsibility directly with affected trade partners.",
      "Carry resolved interfaces into the subcontract exhibits before award.",
    ],
  },
  {
    id: "precon-constructability-workshop",
    title: "Plan a Constructability Workshop",
    outcome: "A targeted workshop agenda that converts design concerns into assigned decisions and actions.",
    role: "Preconstruction Manager",
    category: "Preconstruction",
    tags: ["constructability", "design", "workshop", "planning"],
    estimatedMinutes: 8,
    featured: false,
    variables: [
      { key: "design_milestone", label: "Design milestone", placeholder: "90% construction documents", required: true },
      { key: "focus_areas", label: "Focus areas", placeholder: "Envelope access, MEP routing, structural embeds, turnover phasing", required: true },
      { key: "known_issues", label: "Known issues", placeholder: "Current RFIs, coordination conflicts, and high-risk details", required: true },
      { key: "participants", label: "Participants", placeholder: "Architect, engineers, owner, GC, key trades, commissioning agent", required: true },
      { key: "constraints", label: "Site and schedule constraints", placeholder: "Zero lot line, winter dry-in, phased occupancy", required: true },
    ],
    prompt: `Act as a preconstruction leader preparing a constructability workshop.

Design milestone: {{design_milestone}}
Focus areas: {{focus_areas}}
Known issues: {{known_issues}}
Participants: {{participants}}
Site and schedule constraints: {{constraints}}

Create a 90-minute working agenda prioritized by potential impact to safety, cost, schedule, quality, logistics, and turnover. For each topic, state the decision needed, drawings or models to review, responsible participants, and required output. Include a pre-read checklist and a decision/action log template. Avoid general design commentary; every agenda item must lead to a decision, validation, or assigned follow-up.`,
    tips: [
      "Distribute marked-up details and issue locations before the session.",
      "Invite only participants who can resolve or own the listed interfaces.",
      "Publish decisions and drawing impacts within one working day.",
    ],
  },
  {
    id: "precon-long-lead-plan",
    title: "Build a Long-Lead Procurement Plan",
    outcome: "A prioritized procurement plan tied to design release, approvals, fabrication, logistics, and installation need dates.",
    role: "Preconstruction Manager",
    category: "Preconstruction",
    tags: ["procurement", "long-lead", "schedule", "buyout"],
    estimatedMinutes: 12,
    featured: true,
    variables: [
      { key: "milestones", label: "Project milestones", placeholder: "Permit, mobilization, dry-in, energization, TCO", required: true },
      { key: "lead_items", label: "Potential long-lead items", placeholder: "Switchgear, elevators, curtain wall, AHUs, generators", required: true },
      { key: "design_release", label: "Design release status", placeholder: "Expected package dates and unresolved selections", required: true },
      { key: "market_inputs", label: "Supplier and market inputs", placeholder: "Current quoted lead times, capacity warnings, approved manufacturers", required: true },
      { key: "contract_constraints", label: "Contract constraints", placeholder: "Early-release authority, insurance, deposits, cancellation terms", required: false },
    ],
    prompt: `Act as a preconstruction procurement lead.

Project milestones: {{milestones}}
Potential long-lead items: {{lead_items}}
Design release status: {{design_release}}
Supplier and market inputs: {{market_inputs}}
Contract constraints: {{contract_constraints}}

Create a long-lead procurement register. For each item, identify the field need date, delivery buffer, logistics duration, fabrication duration, submittal and approval duration, bid and award duration, required design release, and latest responsible release date. Flag dependencies, approved-equal constraints, storage needs, deposits, cancellation exposure, and available mitigation. Rank immediate decisions and assign owners. Clearly distinguish supplier-verified lead times from planning assumptions.`,
    tips: [
      "Confirm lead times in writing with qualified suppliers.",
      "Tie each procurement date to the current integrated project schedule.",
      "Review deposits, title, storage, and cancellation terms before early release.",
    ],
  },
  {
    id: "pm-lookahead-constraint-review",
    title: "Turn a Lookahead into a Constraint Plan",
    outcome: "An actionable six-week constraint log with clear owners and release dates.",
    role: "Project Manager",
    category: "Project Management",
    tags: ["lookahead", "constraints", "schedule", "coordination"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "lookahead", label: "Lookahead activities", placeholder: "Paste the next six weeks of activities and dates", required: true },
      { key: "open_items", label: "Open items", placeholder: "RFIs, submittals, procurement, permits, inspections, access", required: true },
      { key: "milestones", label: "Protected milestones", placeholder: "Dry-in, permanent power, commissioning start", required: true },
      { key: "team_inputs", label: "Trade and field inputs", placeholder: "Known commitments, handoffs, and crew assumptions", required: false },
    ],
    prompt: `Act as a project manager preparing the weekly production planning meeting.

Six-week lookahead: {{lookahead}}
Open items: {{open_items}}
Protected milestones: {{milestones}}
Trade and field inputs: {{team_inputs}}

Create a constraint plan organized by activity. Identify each prerequisite involving design, submittals, procurement, labor, equipment, access, predecessor work, permits, inspections, owner decisions, and weather protection. Assign a single owner and a release date before the activity start. Highlight constraints that threaten a protected milestone and propose the next practical action. Also identify activities that appear ready but lack objective confirmation.`,
    tips: [
      "Reconcile the lookahead with the current CPM schedule before use.",
      "Ask owners to confirm release dates rather than assigning them silently.",
      "Close constraints only when the prerequisite is verifiably available.",
    ],
  },
  {
    id: "pm-owner-meeting-brief",
    title: "Prepare an Owner Meeting Brief",
    outcome: "A concise meeting brief centered on decisions, commitments, and changed project conditions.",
    role: "Project Manager",
    category: "Project Management",
    tags: ["owner", "meeting", "decisions", "reporting"],
    estimatedMinutes: 8,
    featured: false,
    variables: [
      { key: "period", label: "Reporting period", placeholder: "Week ending September 18", required: true },
      { key: "status_inputs", label: "Current status inputs", placeholder: "Schedule, cost, procurement, design, safety, quality, closeout", required: true },
      { key: "decisions", label: "Decisions needed", placeholder: "Selections, change approvals, access, funding, or design direction", required: true },
      { key: "prior_actions", label: "Prior action log", placeholder: "Previous commitments, owners, and due dates", required: false },
      { key: "tone", label: "Relationship context", placeholder: "Collaborative but concerned about late design responses", required: false },
    ],
    prompt: `Act as the project manager preparing an owner meeting for {{period}}.

Current status inputs: {{status_inputs}}
Decisions needed: {{decisions}}
Prior action log: {{prior_actions}}
Relationship context: {{tone}}

Create a meeting brief with: executive summary; what changed since the last meeting; milestone outlook; decisions required with impact and decision-by date; commercial items; design and procurement exceptions; safety and quality exceptions; and prior commitments that are late or complete. Draft a focused agenda and an updated action log. Use neutral, factual language. Do not hide adverse information or characterize disputed responsibility as settled fact.`,
    tips: [
      "Align dates and financial figures with the current controls reports.",
      "Pre-wire sensitive decisions with the appropriate project leaders.",
      "Record the final decision and accountable owner in the meeting minutes.",
    ],
  },
  {
    id: "pm-rfi-priority-review",
    title: "Prioritize the RFI Log",
    outcome: "A ranked RFI action list based on field need, downstream impact, and response quality.",
    role: "Project Manager",
    category: "Project Management",
    tags: ["RFI", "design", "schedule", "coordination"],
    estimatedMinutes: 10,
    featured: false,
    variables: [
      { key: "rfi_log", label: "RFI log", placeholder: "Paste RFI number, subject, status, dates, and response", required: true },
      { key: "field_dates", label: "Field need dates", placeholder: "Relevant installation and procurement dates", required: true },
      { key: "milestones", label: "Milestone context", placeholder: "Critical and near-critical milestones", required: true },
      { key: "known_impacts", label: "Known impacts", placeholder: "Trade holds, rework exposure, pending pricing", required: false },
    ],
    prompt: `Act as a project manager conducting an RFI risk review.

RFI log: {{rfi_log}}
Field and procurement need dates: {{field_dates}}
Milestone context: {{milestones}}
Known impacts: {{known_impacts}}

Rank the RFIs by urgency using field need date, critical-path proximity, procurement effect, rework risk, cost exposure, and dependency count. Identify answered RFIs whose response is incomplete, conditional, or likely to require a follow-up. For the highest-priority items, draft a concise escalation note stating the question, required decision, impact of delay, and response deadline. Flag any impact assessment that requires scheduler, estimator, or trade validation.`,
    tips: [
      "Use actual field need dates rather than the RFI due date alone.",
      "Have the affected superintendent and trade verify constructability impacts.",
      "Do not treat a design response as authorization for added cost or time.",
    ],
  },
  {
    id: "pm-change-event-control",
    title: "Structure a Change Event",
    outcome: "A complete change-event record connecting direction, scope, pricing, schedule, notice, and approval status.",
    role: "Project Manager",
    category: "Project Management",
    tags: ["change-management", "cost", "schedule", "documentation"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "event", label: "Changed condition or direction", placeholder: "Owner-directed lobby finish revision issued after procurement", required: true },
      { key: "records", label: "Source records", placeholder: "Directive, RFI, sketches, meeting minutes, correspondence", required: true },
      { key: "cost_inputs", label: "Cost inputs", placeholder: "Trade pricing, allowances, credits, GC costs", required: false },
      { key: "schedule_inputs", label: "Schedule inputs", placeholder: "Affected activities, procurement dates, float, milestones", required: false },
      { key: "contract_rules", label: "Contract requirements", placeholder: "Notice period, pricing format, authorization rules", required: true },
    ],
    prompt: `Act as a project manager establishing a defensible change-event record.

Changed condition or direction: {{event}}
Source records: {{records}}
Cost inputs: {{cost_inputs}}
Schedule inputs: {{schedule_inputs}}
Contract requirements: {{contract_rules}}

Create a change-event summary with chronology, originating document, scope delta, affected trades, cost status, schedule status, notice status, authorization status, assumptions, exclusions, open questions, and next action. Separate directed work from proposed work and verified cost from pending estimates. Identify missing records needed to preserve contractual rights. Draft a short internal routing note, but do not make legal conclusions.`,
    tips: [
      "Preserve original source records and issue dates with the event.",
      "Have commercial and scheduling leads validate entitlement and time impact.",
      "Do not instruct extra work without authority permitted by the contract and company policy.",
    ],
  },
  {
    id: "field-daily-plan",
    title: "Build Tomorrow's Field Plan",
    outcome: "A coordinated daily plan covering work areas, crews, prerequisites, interfaces, and controls.",
    role: "Superintendent",
    category: "Field Operations",
    tags: ["daily-plan", "production", "coordination", "field"],
    estimatedMinutes: 8,
    featured: true,
    variables: [
      { key: "activities", label: "Planned activities", placeholder: "Work areas, trades, crew sizes, quantities, and targets", required: true },
      { key: "site_conditions", label: "Site conditions", placeholder: "Access, weather, occupied areas, deliveries, temporary systems", required: true },
      { key: "constraints", label: "Known constraints", placeholder: "Design holds, material status, inspections, predecessor work", required: true },
      { key: "safety_quality", label: "Safety and quality requirements", placeholder: "Permits, AHAs, hold points, mockups, testing", required: true },
    ],
    prompt: `Act as a lead superintendent coordinating tomorrow's work.

Planned activities: {{activities}}
Site conditions: {{site_conditions}}
Known constraints: {{constraints}}
Safety and quality requirements: {{safety_quality}}

Produce a practical daily field plan by area and shift. For each activity, list crew, target quantity, prerequisites, material and equipment status, access, trade interfaces, inspection or hold point, safety control, and responsible foreperson. Identify conflicts and no-go conditions before work starts. End with the five questions to resolve at the daily huddle and the conditions requiring escalation.`,
    tips: [
      "Confirm the plan with trade forepersons before the shift begins.",
      "Use current permits, approved documents, and inspection requirements.",
      "Stop and re-plan when actual site conditions differ materially from the inputs.",
    ],
  },
  {
    id: "field-logistics-sequencing",
    title: "Coordinate Site Logistics and Deliveries",
    outcome: "A time-phased logistics plan that exposes access, lifting, storage, and trade conflicts.",
    role: "Superintendent",
    category: "Field Operations",
    tags: ["logistics", "deliveries", "crane", "site-planning"],
    estimatedMinutes: 10,
    featured: false,
    variables: [
      { key: "site_layout", label: "Site layout and restrictions", placeholder: "Gates, roads, crane zones, hoists, laydown, occupied areas", required: true },
      { key: "delivery_plan", label: "Planned deliveries", placeholder: "Dates, loads, suppliers, offload method, destination", required: true },
      { key: "work_plan", label: "Concurrent work", placeholder: "Activities and crews operating near delivery routes or lift zones", required: true },
      { key: "restrictions", label: "External restrictions", placeholder: "Permits, neighborhood hours, street closures, owner rules", required: true },
    ],
    prompt: `Act as a site logistics superintendent.

Site layout and restrictions: {{site_layout}}
Planned deliveries: {{delivery_plan}}
Concurrent work: {{work_plan}}
External restrictions: {{restrictions}}

Create a time-phased logistics plan. Identify delivery windows, route, gate, staging location, offload equipment, spotter requirements, lift-zone controls, temporary storage, final destination, and responsible coordinator. Detect conflicts involving cranes, hoists, emergency access, public protection, occupied operations, and concurrent trades. Recommend resequencing where required and list confirmations needed 48 hours and 24 hours before delivery.`,
    tips: [
      "Validate dimensions, weights, and equipment capacities from approved sources.",
      "Coordinate traffic and public protection requirements with authorized personnel.",
      "Communicate revisions to every affected trade and gate coordinator.",
    ],
  },
  {
    id: "field-workface-readiness",
    title: "Check Workface Readiness",
    outcome: "A go/no-go readiness check before labor and equipment are committed to an installation area.",
    role: "Superintendent",
    category: "Field Operations",
    tags: ["readiness", "productivity", "workface", "constraints"],
    estimatedMinutes: 7,
    featured: false,
    variables: [
      { key: "activity", label: "Activity", placeholder: "Install Level 8 overhead MEP mains", required: true },
      { key: "location_date", label: "Location and planned start", placeholder: "Level 8 west, Monday September 21", required: true },
      { key: "readiness_inputs", label: "Readiness inputs", placeholder: "Drawings, materials, labor, equipment, access, predecessor status", required: true },
      { key: "controls", label: "Required controls", placeholder: "Permits, inspections, testing, quality hold points, safety plans", required: true },
    ],
    prompt: `Act as a superintendent performing a workface readiness review.

Activity: {{activity}}
Location and planned start: {{location_date}}
Readiness inputs: {{readiness_inputs}}
Required controls: {{controls}}

Evaluate readiness across approved information, material availability, labor, equipment, access, predecessor completion, temporary conditions, trade coordination, permits, safety planning, quality requirements, and inspection availability. Rate each item READY, AT RISK, or NOT READY and cite the supplied evidence. Give a final GO, CONDITIONAL GO, or NO-GO recommendation with the exact release actions, owners, and deadlines.`,
    tips: [
      "Verify readiness at the physical work area, not only in the meeting room.",
      "Treat unapproved documents or unconfirmed material as not released.",
      "Do not mobilize a crew against unresolved no-go conditions.",
    ],
  },
  {
    id: "field-production-recovery",
    title: "Create a Production Recovery Plan",
    outcome: "A measurable short-interval plan to recover an installation that is missing production targets.",
    role: "Superintendent",
    category: "Field Operations",
    tags: ["production", "recovery", "labor", "planning"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "work_scope", label: "Work scope", placeholder: "Metal stud framing, Levels 5-8", required: true },
      { key: "performance", label: "Planned vs. actual performance", placeholder: "1,200 LF/week planned; 760 LF/week actual", required: true },
      { key: "observations", label: "Field observations", placeholder: "Crew mix, waits, rework, access, material flow, supervision", required: true },
      { key: "milestone", label: "Required milestone", placeholder: "Level 8 ready for rough-in by October 9", required: true },
      { key: "constraints", label: "Recovery constraints", placeholder: "Overtime limits, occupied hours, available crews and equipment", required: true },
    ],
    prompt: `Act as a senior superintendent recovering production performance.

Work scope: {{work_scope}}
Planned versus actual performance: {{performance}}
Field observations: {{observations}}
Required milestone: {{milestone}}
Recovery constraints: {{constraints}}

Diagnose likely causes under labor, methods, materials, information, access, equipment, coordination, quality, and supervision. Distinguish evidence from hypotheses. Create a two-week recovery plan with daily measurable targets, crew and zone strategy, prerequisite releases, responsible owners, and check-in points. Show how the plan supports the milestone and define the trigger for changing strategy if production does not improve.`,
    tips: [
      "Validate productivity quantities and labor hours with the trade partner.",
      "Address flow and constraint causes before defaulting to overtime.",
      "Monitor quality and safety indicators alongside production gains.",
    ],
  },
  {
    id: "commercial-notice-draft",
    title: "Draft a Contract Notice for Review",
    outcome: "A factual notice draft that preserves the event chronology, potential impact, and requested response.",
    role: "Commercial Manager",
    category: "Commercial/Contracts",
    tags: ["notice", "contract", "change", "documentation"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "contract_clause", label: "Relevant contract clause", placeholder: "Section 8.3 notice and claims requirements", required: true },
      { key: "event", label: "Event or condition", placeholder: "Late release of approved-for-fabrication curtain wall details", required: true },
      { key: "chronology", label: "Chronology and records", placeholder: "Dates, correspondence, RFIs, directives, and schedule updates", required: true },
      { key: "potential_impact", label: "Potential impact", placeholder: "Fabrication slot, crane sequence, tenant access, cost", required: true },
      { key: "requested_action", label: "Requested action", placeholder: "Issue final details by September 18 and meet to mitigate", required: true },
    ],
    prompt: `Act as a construction commercial manager preparing a contract notice for legal and project review.

Relevant clause: {{contract_clause}}
Event or condition: {{event}}
Chronology and source records: {{chronology}}
Potential impact: {{potential_impact}}
Requested action: {{requested_action}}

Draft a concise, professional notice that identifies the contract and event, states verified chronology, describes actual and potential effects without overstatement, references the applicable notice requirement, reserves rights appropriately, states ongoing mitigation, requests the required action, and identifies follow-up information to be provided. Mark factual gaps and unsupported causation statements for review. Do not provide legal advice or invent entitlement.`,
    tips: [
      "Have project leadership and qualified counsel review before issuance.",
      "Verify notice method, recipient, and deadline directly from the executed contract.",
      "Attach or preserve the contemporaneous records supporting each factual statement.",
    ],
  },
  {
    id: "commercial-change-order-review",
    title: "Review a Change Order Proposal",
    outcome: "A transparent assessment of scope, entitlement, price, time, and documentation gaps.",
    role: "Commercial Manager",
    category: "Commercial/Contracts",
    tags: ["change-order", "pricing", "entitlement", "audit"],
    estimatedMinutes: 12,
    featured: false,
    variables: [
      { key: "proposal", label: "Change proposal", placeholder: "Paste scope narrative, pricing, markups, exclusions, and schedule request", required: true },
      { key: "contract_basis", label: "Contract basis", placeholder: "Relevant scope, drawings, specifications, and change clauses", required: true },
      { key: "directive", label: "Change source", placeholder: "Directive, RFI response, bulletin, or changed condition record", required: true },
      { key: "project_records", label: "Project records", placeholder: "Daily reports, labor records, invoices, schedule updates", required: false },
    ],
    prompt: `Act as a construction commercial manager reviewing a change order proposal.

Proposal: {{proposal}}
Contract basis: {{contract_basis}}
Change source: {{directive}}
Project records: {{project_records}}

Assess scope causation, contractual basis, authorization, quantity support, labor rates and hours, material and equipment support, subcontractor tiers, markups, credits, overlap with base scope, schedule effect, concurrency, and mitigation. Separate undisputed scope from disputed or unsupported elements. Produce a review table with proposed amount, supported amount, open amount, required backup, and negotiation position. Identify questions for project controls, field leadership, and counsel without making legal conclusions.`,
    tips: [
      "Reconcile the proposal against executed scope and prior change events.",
      "Confirm schedule impact through a qualified time-impact review when material.",
      "Document negotiated changes and assumptions in the final modification.",
    ],
  },
  {
    id: "commercial-subcontract-risk-review",
    title: "Review Subcontract Commercial Risk",
    outcome: "A focused risk register for subcontract terms, scope, cash flow, schedule, and performance obligations.",
    role: "Commercial Manager",
    category: "Commercial/Contracts",
    tags: ["subcontract", "risk", "buyout", "terms"],
    estimatedMinutes: 12,
    featured: false,
    variables: [
      { key: "subcontract_scope", label: "Subcontract scope", placeholder: "Trade scope, alternates, exclusions, and clarifications", required: true },
      { key: "proposed_terms", label: "Proposed terms", placeholder: "Payment, indemnity, schedule, changes, insurance, warranty, termination", required: true },
      { key: "prime_requirements", label: "Prime contract flow-downs", placeholder: "Applicable owner contract obligations", required: true },
      { key: "trade_profile", label: "Trade partner profile", placeholder: "Capacity, financial standing, past performance, current workload", required: false },
      { key: "project_risks", label: "Project-specific risks", placeholder: "Phasing, design status, procurement, labor, logistics", required: true },
    ],
    prompt: `Act as a construction commercial manager supporting subcontract award.

Subcontract scope: {{subcontract_scope}}
Proposed terms: {{proposed_terms}}
Prime contract flow-downs: {{prime_requirements}}
Trade partner profile: {{trade_profile}}
Project-specific risks: {{project_risks}}

Create a commercial risk register covering scope interfaces, exclusions, schedule obligations, damages, changes, payment, retainage, bonds, insurance, indemnity, warranty, design responsibility, safety, closeout, termination, and flow-down alignment. Rate each item by severity and negotiability. Recommend specific clarifications, exhibits, or approvals required before award. Flag issues requiring legal, insurance, treasury, or executive review.`,
    tips: [
      "Use the current negotiated subcontract and executed prime contract language.",
      "Route legal interpretations to qualified counsel.",
      "Resolve material scope and term exceptions before issuing the award.",
    ],
  },
  {
    id: "commercial-claim-chronology",
    title: "Build a Claim Event Chronology",
    outcome: "An evidence-based chronology connecting events, notice, response, mitigation, and alleged effects.",
    role: "Commercial Manager",
    category: "Commercial/Contracts",
    tags: ["claim", "chronology", "evidence", "dispute"],
    estimatedMinutes: 15,
    featured: true,
    variables: [
      { key: "issue", label: "Issue under review", placeholder: "Delayed structural redesign and resulting steel resequencing", required: true },
      { key: "records", label: "Project records", placeholder: "Correspondence, RFIs, submittals, minutes, notices, schedules, daily reports", required: true },
      { key: "contract_milestones", label: "Contract milestones", placeholder: "Baseline and approved milestone dates", required: true },
      { key: "positions", label: "Known party positions", placeholder: "Owner, designer, contractor, and trade statements", required: false },
    ],
    prompt: `Act as a construction claims analyst organizing facts for professional review.

Issue: {{issue}}
Project records: {{records}}
Contract milestones: {{contract_milestones}}
Known party positions: {{positions}}

Create a neutral event chronology with date, source, event, party, required or promised action, actual response, alleged effect, and evidence reference. Separate contemporaneous fact, later assertion, analysis, and unresolved conflict. Identify notice events, mitigation steps, schedule updates, and gaps in the record. Close with a list of records and specialist analyses still needed. Do not state legal entitlement or causation as fact unless directly supported by the supplied record.`,
    tips: [
      "Preserve original timestamps, authors, and document references.",
      "Have schedule, cost, and legal specialists review conclusions in their disciplines.",
      "Keep advocacy out of the chronology; reserve it for a separate position paper.",
    ],
  },
  {
    id: "safety-pretask-plan-review",
    title: "Review a High-Risk Pre-Task Plan",
    outcome: "A practical challenge review that identifies missing controls before high-risk work begins.",
    role: "Safety and Quality Leader",
    category: "Safety/Quality",
    tags: ["safety", "pre-task", "high-risk", "controls"],
    estimatedMinutes: 10,
    featured: true,
    variables: [
      { key: "work_activity", label: "Work activity", placeholder: "Set rooftop AHUs with a mobile crane", required: true },
      { key: "work_plan", label: "Proposed work plan", placeholder: "Sequence, crew, equipment, access, communication, and duration", required: true },
      { key: "site_conditions", label: "Site conditions", placeholder: "Weather, public interface, overhead hazards, adjacent work", required: true },
      { key: "controls", label: "Proposed controls", placeholder: "Permits, lift plan, barricades, fall protection, emergency plan", required: true },
      { key: "requirements", label: "Applicable requirements", placeholder: "Company policy, manufacturer guidance, project rules, regulations", required: true },
    ],
    prompt: `Act as a construction safety leader performing a challenge review before work begins.

Work activity: {{work_activity}}
Proposed plan: {{work_plan}}
Site conditions: {{site_conditions}}
Proposed controls: {{controls}}
Applicable requirements: {{requirements}}

Review the task sequence for credible hazards, exposure points, simultaneous operations, equipment limits, competency requirements, exclusion zones, communication failures, changing conditions, emergency response, and stop-work triggers. Identify missing information and controls. Produce questions for the competent person and crew briefing. Do not declare the plan safe or compliant; final approval remains with authorized site personnel.`,
    tips: [
      "Conduct the final review at the work location with the performing crew.",
      "Use current regulatory, manufacturer, and company requirements.",
      "Stop work when conditions or equipment differ from the approved plan.",
    ],
  },
  {
    id: "quality-inspection-test-plan",
    title: "Create an Inspection and Test Plan",
    outcome: "A traceable quality plan with acceptance criteria, hold points, records, and responsible parties.",
    role: "Safety and Quality Leader",
    category: "Safety/Quality",
    tags: ["quality", "inspection", "testing", "ITP"],
    estimatedMinutes: 12,
    featured: true,
    variables: [
      { key: "scope", label: "Installation scope", placeholder: "Below-grade waterproofing at podium expansion joints", required: true },
      { key: "requirements", label: "Contract requirements", placeholder: "Specifications, drawings, approved submittals, mockups", required: true },
      { key: "sequence", label: "Installation sequence", placeholder: "Substrate prep through protection and cover", required: true },
      { key: "participants", label: "Responsible participants", placeholder: "Trade, GC, consultant, testing agency, owner", required: true },
      { key: "known_risks", label: "Known quality risks", placeholder: "Concealment, weather, transitions, incompatible materials", required: false },
    ],
    prompt: `Act as a construction quality manager preparing an inspection and test plan.

Installation scope: {{scope}}
Contract requirements: {{requirements}}
Installation sequence: {{sequence}}
Responsible participants: {{participants}}
Known quality risks: {{known_risks}}

Create an inspection and test plan by work step. Include prerequisite documents, material verification, environmental conditions, preparatory meeting, mockup, first-work inspection, in-process checks, tests, measurable acceptance criteria, witness and hold points, responsible party, required record, nonconformance response, and release for concealment. Cite the supplied requirement for each criterion where possible and flag criteria that require verification.`,
    tips: [
      "Confirm acceptance criteria against the latest approved contract documents.",
      "Do not conceal work until designated hold points are formally released.",
      "Retain photos, test results, and sign-offs in the project quality record.",
    ],
  },
  {
    id: "quality-nonconformance-response",
    title: "Structure a Nonconformance Response",
    outcome: "A disciplined NCR response separating containment, disposition, root cause, and prevention.",
    role: "Safety and Quality Leader",
    category: "Safety/Quality",
    tags: ["quality", "NCR", "root-cause", "corrective-action"],
    estimatedMinutes: 10,
    featured: false,
    variables: [
      { key: "condition", label: "Nonconforming condition", placeholder: "Anchor bolts installed outside specified tolerance", required: true },
      { key: "requirement", label: "Applicable requirement", placeholder: "Drawing detail, specification tolerance, approved submittal", required: true },
      { key: "extent", label: "Known extent", placeholder: "Eight locations inspected; three affected; remaining locations pending", required: true },
      { key: "immediate_actions", label: "Immediate actions", placeholder: "Work stopped, area protected, engineer notified", required: true },
      { key: "records", label: "Available records", placeholder: "Survey, photos, inspection report, installation records", required: true },
    ],
    prompt: `Act as a construction quality manager structuring a nonconformance response.

Observed condition: {{condition}}
Applicable requirement: {{requirement}}
Known extent: {{extent}}
Immediate actions: {{immediate_actions}}
Available records: {{records}}

Prepare an NCR response with objective description, requirement, affected locations, containment, extent-of-condition review, disposition options requiring authorized design input, verification steps, likely contributing factors, root-cause questions, corrective action, preventive action, owner, and closeout evidence. Clearly distinguish repair proposals from approved dispositions. Do not approve engineering changes or acceptance of nonconforming work.`,
    tips: [
      "Preserve the condition and evidence until authorized evaluation is complete.",
      "Obtain disposition from the party contractually authorized to accept or redesign the work.",
      "Verify effectiveness beyond the originally observed locations.",
    ],
  },
  {
    id: "safety-quality-learning-review",
    title: "Run a Learning Review After an Event",
    outcome: "A blame-aware learning review that identifies system conditions and practical preventive actions.",
    role: "Safety and Quality Leader",
    category: "Safety/Quality",
    tags: ["learning-review", "incident", "quality-event", "improvement"],
    estimatedMinutes: 12,
    featured: false,
    variables: [
      { key: "event", label: "Event", placeholder: "Dropped material near miss during facade unloading", required: true },
      { key: "timeline", label: "Known timeline", placeholder: "What occurred before, during, and immediately after", required: true },
      { key: "conditions", label: "Work conditions", placeholder: "Plan, staffing, equipment, weather, coordination, production pressure", required: true },
      { key: "evidence", label: "Available evidence", placeholder: "Interviews, photos, permits, plans, training, equipment records", required: true },
      { key: "response", label: "Actions already taken", placeholder: "Area secured, work paused, equipment inspected", required: false },
    ],
    prompt: `Act as a construction safety and quality learning-review facilitator.

Event: {{event}}
Known timeline: {{timeline}}
Work conditions: {{conditions}}
Available evidence: {{evidence}}
Actions already taken: {{response}}

Prepare a learning review that reconstructs the work as performed, compares normal practice with the written plan, and examines planning, supervision, equipment, environment, communication, interfaces, workload, and organizational conditions. Separate facts, participant perspectives, and hypotheses. Develop interview questions and practical corrective actions using the hierarchy of controls where applicable. Avoid assigning blame or claiming a root cause before evidence supports it.`,
    tips: [
      "Follow company reporting, preservation, and regulatory notification requirements.",
      "Use trained investigators for serious events and protect sensitive information.",
      "Track corrective actions to verified completion and effectiveness.",
    ],
  },
] as const satisfies readonly ConstructionPrompt[];

export type ConstructionPromptId = (typeof constructionPrompts)[number]["id"];

export const promptLibrary: readonly ConstructionPrompt[] = constructionPrompts;

export const promptsByCategory = PROMPT_CATEGORIES.reduce(
  (groups, category) => {
    groups[category] = constructionPrompts.filter((prompt) => prompt.category === category);
    return groups;
  },
  {} as Record<CanonicalPromptCategory, readonly ConstructionPrompt[]>,
);
